import { useEffect, useMemo, useRef, useState } from 'react';

import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { DropdownFormElementAnswerSchema, DropdownFormElement as DropdownFormElementType } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useQuery } from '@tanstack/react-query';
import ReactSelect from 'react-select';
import { z } from 'zod';

import CloseIcon from '@/components/icons/close';
import { GripDotVerticalIcon } from '@/components/icons/grip-dot-vertical';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MULTI_SELECT_STYLES } from '@/components/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DoubleClickInput, SELECT_COMPONENTS } from '@/components/ui/select';
import { getListOptionsQueryOptions, getListsQueryOptions } from '@/queries/lists';
import { useIsAWA } from '@/utils/awa';

import { DropdownConfigLabels } from '../config-field-labels';
import { ConfigLabel } from '../config-label';
import { FormFieldError } from '../form-field-error';

// A stored Dropdown answer is a string[], a single string, or an empty value; normalise to a string[].
function toValueArray(value: z.infer<typeof DropdownFormElementAnswerSchema>['value']): string[] {
  if (Array.isArray(value)) {
    return value;
  }
  return value === undefined || value === null || value === '' ? [] : [value];
}

export const DropdownFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  error,
}: {
  element: DropdownFormElementType;
  value?: z.infer<typeof DropdownFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof DropdownFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly?: boolean;
  error?: string;
}) => {
  const { isAWA } = useIsAWA();

  const ref = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const listId = element.config?.source === 'list' ? element.config.listId : '';
  const { data: fetchedOptions, isLoading } = useQuery({
    ...getListOptionsQueryOptions({ listId, isAWA }),
    enabled: element.config?.source === 'list' && !!listId,
  });

  const options = useMemo(() => {
    if (element.config?.source === 'list') {
      return fetchedOptions?.map((opt: string) => ({ label: opt, value: opt })) ?? [];
    }
    if (element.config?.source === 'static') {
      return element.config.options.map((opt: string) => ({ label: opt, value: opt }));
    }
    return [];
  }, [element.config, fetchedOptions]);

  // A submitted answer is stored as plain text, independent of the current options, so an option deleted
  // after the record was filled leaves a stored value with no matching option — which would render blank,
  // silently hiding a real answer. In readonly, synthesise an option per orphaned value so the record keeps
  // showing what it captured. Never in edit mode: an editor must only pick from the live options.
  const displayOptions = useMemo(() => {
    if (!readonly) {
      return options;
    }
    const known = new Set(options.map((opt) => opt.value));
    const orphaned = toValueArray(value)
      .filter((stored) => !known.has(stored))
      .map((stored) => ({ label: stored, value: stored }));
    return orphaned.length > 0 ? [...options, ...orphaned] : options;
  }, [readonly, options, value]);

  const handleChange = (selected: { label: string; value: string } | { label: string; value: string }[] | null) => {
    if (Array.isArray(selected)) {
      onChange(selected.map((opt) => opt.value));
    } else if (selected) {
      onChange(selected.value);
    } else {
      onChange(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const className = target?.getAttribute('class') || '';
      const clickedInsideRef = className.includes(element.id);
      if (clickedInsideRef) {
        return;
      }

      setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [element.id]);

  return (
    <div className="space-y-1" ref={ref}>
      {element.label && <Label required={element.required}>{element.label}</Label>}

      <ReactSelect<{ label: string; value: string }>
        // @ts-expect-error - React Select has PITA types for this
        classNames={MULTI_SELECT_STYLES<{ label: string; value: string }>()}
        components={{
          ...SELECT_COMPONENTS<{ label: string; value: string }>(),
          Input: (props) => <DoubleClickInput {...props} setIsMenuOpen={setIsMenuOpen} />,
        }}
        unstyled
        // @ts-expect-error: React Select has PITA typing for the isMulti prop, address later
        isMulti={element.config?.multiple ?? true}
        blurInputOnSelect
        isClearable={!readonly}
        options={displayOptions}
        value={
          displayOptions?.filter((opt: { label: string; value: string }) => (Array.isArray(value) ? value.includes(opt.value) : value === opt.value)) || null
        }
        onChange={handleChange}
        onBlur={onBlur}
        isDisabled={readonly}
        placeholder={`Select ${element.label}`}
        menuIsOpen={isMenuOpen}
        styles={{
          control: (state) => ({
            ...state,
            borderColor: isMenuOpen ? 'rgb(var(--default-blue))' : 'rgb(var(--grey-3))',
            opacity: '1',
            backgroundColor: readonly ? 'rgb(var(--grey-1))' : state.backgroundColor,
          }),
        }}
        classNamePrefix={element.id}
        className="w-full"
        menuPortalTarget={document.body}
        isLoading={isLoading}
      />

      <FormFieldError error={error} />
    </div>
  );
};

export function DropdownFormElementMock({ element }: { element: DropdownFormElementType }) {
  const { isAWA } = useIsAWA();

  const ref = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [value, setValue] = useState<string | string[] | undefined>(undefined);

  const mockListId = element.config?.source === 'list' ? element.config.listId : '';
  const { data: fetchedOptions, isLoading } = useQuery({
    ...getListOptionsQueryOptions({ listId: mockListId, isAWA }),
    enabled: element.config?.source === 'list' && !!mockListId,
  });

  const options = useMemo(() => {
    if (element.config?.source === 'list') {
      return fetchedOptions?.map((opt: string) => ({ label: opt, value: opt })) ?? [];
    }
    if (element.config?.source === 'static') {
      return element.config.options.map((opt: string) => ({ label: opt, value: opt }));
    }
    return [];
  }, [element.config, fetchedOptions]);

  const handleChange = (selected: { label: string; value: string } | { label: string; value: string }[] | null) => {
    if (Array.isArray(selected)) {
      setValue(selected.map((opt) => opt.value));
    } else if (selected) {
      setValue(selected.value);
    } else {
      setValue(undefined);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const className = target?.getAttribute('class') || '';
      const clickedInsideRef = className.includes(element.id);
      if (clickedInsideRef) {
        return;
      }

      setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [element.id]);

  return (
    <div ref={ref}>
      <ReactSelect<{ label: string; value: string }>
        // @ts-expect-error - React Select has PITA types for this
        classNames={MULTI_SELECT_STYLES<{ label: string; value: string }>()}
        blurInputOnSelect
        components={{
          ...SELECT_COMPONENTS<{ label: string; value: string }>(),
          Input: (props) => <DoubleClickInput {...props} setIsMenuOpen={setIsMenuOpen} />,
        }}
        unstyled
        // @ts-expect-error: React Select has PITA typing for the isMulti prop, address later
        isMulti={element.config?.multiple ?? true}
        options={options}
        value={options?.filter((opt: { label: string; value: string }) => (Array.isArray(value) ? value.includes(opt.value) : value === opt.value)) || null}
        onChange={handleChange}
        isDisabled={false}
        placeholder={`Select ${element.label}`}
        menuIsOpen={isMenuOpen}
        styles={{
          control: (state) => ({
            ...state,
            borderColor: isMenuOpen ? 'rgb(var(--default-blue))' : 'rgb(var(--grey-3))',
          }),
        }}
        classNamePrefix={element.id}
        className="w-full"
        menuPortalTarget={document.body}
        isLoading={isLoading}
      />
    </div>
  );
}

export function DropdownFormElementConfig({
  element,
  onChange,
}: {
  element: DropdownFormElementType;
  onChange?: (updates: Partial<DropdownFormElementType>) => void;
}) {
  const { data: lists } = useQuery(getListsQueryOptions());

  const [newOptionValue, setNewOptionValue] = useState('');
  const [options, setOptions] = useState<{ id: string; value: string }[]>(
    element.config?.source === 'static' && element.config?.options ? element.config.options.map((opt) => ({ id: crypto.randomUUID(), value: opt })) : [],
  );

  const [optionsListRef, optionsList, setOptionsList] = useDragAndDrop<HTMLUListElement, { id: string; value: string }>(options, {
    dragHandle: '.handle',
    onSort: ({ position, previousPosition }) => {
      const newOptions = [...options];
      const [movedItem] = newOptions.splice(previousPosition, 1);
      newOptions.splice(position, 0, movedItem);
      setOptions(newOptions);

      if (element.config?.source === 'static') {
        onChange?.({
          config: {
            ...element.config,
            options: newOptions.map((opt) => opt.value),
          },
        });
      }
    },
  });

  useEffect(() => {
    if (element.config?.source === 'static' && element.config?.options) {
      setOptions(element.config.options.map((opt) => ({ id: crypto.randomUUID(), value: opt })));
    } else {
      setOptions([]);
    }
  }, [element.config]);

  useEffect(() => {
    setOptionsList(options);
  }, [options, setOptionsList]);

  const handleSourceChange = (source: 'static' | 'list') => {
    if (source === 'static') {
      const config = {
        source: 'static' as const,
        options: [],
        multiple: true,
      };
      onChange?.({ config });
      setOptions([]);
    } else {
      const config = {
        source: 'list' as const,
        listId: lists?.[0]?.id ?? '',
        multiple: true,
      };
      onChange?.({ config });
    }
  };

  const handleAddOption = () => {
    if (!newOptionValue || element.config?.source !== 'static') return;
    const newOption = { id: crypto.randomUUID(), value: newOptionValue };
    setOptions([...options, newOption]);
    if (element.config?.source === 'static') {
      onChange?.({
        config: {
          ...element.config,
          options: [...options, newOption].map((opt) => opt.value),
        },
      });
    }
    setNewOptionValue('');
  };

  const handleRemoveOption = (optionToRemove: { id: string; value: string }) => {
    if (element.config?.source !== 'static') return;
    const newOptions = options.filter((opt) => opt.id !== optionToRemove.id);
    setOptions(newOptions);
    if (element.config?.source === 'static') {
      onChange?.({
        config: {
          ...element.config,
          options: newOptions.map((opt) => opt.value),
        },
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>

      {/* {element.config?.source === 'list' && (
        <ConfigLabel className="flex-row items-start gap-2">
          <Checkbox
            checked={element.config?.multiple ?? false}
            onCheckedChange={(checked) =>
              onChange?.({
                config: {
                  ...element.config,
                  multiple: !!checked,
                },
              })
            }
          />
          <span>Allow multiple selection</span>
        </ConfigLabel>
      )} */}

      <div className="space-y-1">
        <Label className="text-xs font-medium">{DropdownConfigLabels.source}: </Label>
        <RadioGroup value={element.config?.source ?? 'static'} onValueChange={handleSourceChange}>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="static" id="static" />
            <Label htmlFor="static" className="text-xs">
              {DropdownConfigLabels.sourceStatic}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="list" id="list" />
            <Label htmlFor="list" className="text-xs">
              {DropdownConfigLabels.sourceList}
            </Label>
          </div>
        </RadioGroup>
      </div>

      {element.config?.source === 'static' && (
        <div className="space-y-1">
          <Label className="text-xs font-medium">{DropdownConfigLabels.options}:</Label>
          <div className="flex gap-2">
            <Input
              value={newOptionValue}
              onChange={(e) => setNewOptionValue(e.target.value)}
              placeholder="Add list item..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddOption();
                }
              }}
            />
            <Button type="button" onClick={handleAddOption} disabled={!newOptionValue}>
              Add
            </Button>
          </div>

          <ul ref={optionsListRef} className="space-y-1">
            {optionsList.map((option) => (
              <li key={option.id}>
                <div className="handle border-grey-3 flex cursor-move items-center gap-2 rounded-md border p-1 text-sm">
                  <GripDotVerticalIcon className="text-secondary-text" />
                  <span className="flex-1">{option.value}</span>
                  <Button type="button" variant="ghost" size="sm" aria-label="Remove option" onClick={() => handleRemoveOption(option)}>
                    <CloseIcon className="text-default-red size-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {element.config?.source === 'list' && (
        <div className="space-y-2">
          <Label className="text-xs font-medium">{DropdownConfigLabels.listId}</Label>
          <ReactSelect<{ label: string; value: string }>
            // @ts-expect-error - React Select has PITA types for this
            classNames={MULTI_SELECT_STYLES<{ label: string; value: string }>()}
            components={{
              ...SELECT_COMPONENTS<{ label: string; value: string }>(),
            }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            unstyled
            options={lists?.map((list) => ({ label: list.name, value: list.id })) ?? []}
            value={
              element.config?.source === 'list' && lists?.find((list) => list.id === (element.config as { listId: string }).listId)
                ? {
                    label: lists.find((list) => list.id === (element.config as { listId: string }).listId)!.name,
                    value: (element.config as { listId: string }).listId,
                  }
                : null
            }
            onChange={(selected) =>
              onChange?.({
                config:
                  element.config?.source === 'list'
                    ? {
                        ...element.config,
                        listId: selected?.value ?? '',
                      }
                    : element.config,
              })
            }
            placeholder="Select a list..."
            className="w-full"
          />
        </div>
      )}
    </div>
  );
}
